import type { AmbientMood } from '../ids.js';

/**
 * The ambient score, generated — no note data files yet (the operator asked for procedural for
 * now; authored note data can replace a mood later without touching callers). Each mood is a slow
 * chord pad plus sparse pentatonic plucks, seeded so a given device plays the same evolving loop,
 * mixed well under the SFX. The voice schedules ahead in short windows and stops with a gentle
 * fade — never abruptly (spec §Ambient music).
 */

interface MoodSpec {
  /** Chord roots (Hz), one per slow step. */
  readonly padChords: ReadonlyArray<readonly [number, number, number]>;
  /** Pentatonic pool for the plucks (Hz). */
  readonly plucks: readonly number[];
  /** Average seconds between plucks. */
  readonly pluckEvery: number;
  readonly padGain: number;
}

// All moods sit in one warm palette (F Lydian-ish neighbourhood) so transitions never jar.
const MOODS: Record<AmbientMood, MoodSpec> = {
  menu: {
    padChords: [[174.6, 261.6, 349.2], [196.0, 293.7, 392.0], [130.8, 261.6, 329.6], [155.6, 233.1, 349.2]],
    plucks: [523.3, 587.3, 698.5, 784.0, 880.0],
    pluckEvery: 3.2,
    padGain: 0.05,
  },
  chess: {
    padChords: [[130.8, 196.0, 329.6], [146.8, 220.0, 349.2], [174.6, 261.6, 415.3], [123.5, 196.0, 311.1]],
    plucks: [392.0, 440.0, 523.3, 587.3, 659.3],
    pluckEvery: 4.0,
    padGain: 0.045,
  },
  checkers: {
    padChords: [[196.0, 293.7, 392.0], [174.6, 261.6, 349.2], [220.0, 329.6, 440.0], [164.8, 246.9, 392.0]],
    plucks: [523.3, 587.3, 659.3, 784.0, 880.0, 1046.5],
    pluckEvery: 2.4,
    padGain: 0.05,
  },
  backgammon: {
    padChords: [[155.6, 233.1, 311.1], [174.6, 261.6, 349.2], [146.8, 220.0, 293.7], [185.0, 277.2, 349.2]],
    plucks: [466.2, 523.3, 622.3, 698.5, 784.0],
    pluckEvery: 2.8,
    padGain: 0.05,
  },
  cards: {
    padChords: [[164.8, 246.9, 329.6], [146.8, 220.0, 293.7], [185.0, 277.2, 369.9], [138.6, 207.7, 329.6]],
    plucks: [440.0, 493.9, 587.3, 659.3, 740.0],
    pluckEvery: 3.0,
    padGain: 0.045,
  },
};

/** Seconds scheduled ahead of the playhead on each tick. */
const LOOKAHEAD = 4;

export class AmbientVoice {
  private chordIndex = 0;
  private nextChordAt = 0;
  private nextPluckAt = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private padOscs: readonly OscillatorNode[] = [];
  private padGain: GainNode | null = null;
  private stopped = false;

  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly dest: AudioNode,
    private readonly mood: AmbientMood,
    private readonly random: () => number,
  ) {}

  start(): void {
    const spec = MOODS[this.mood];
    const ctx = this.ctx;
    const now = ctx.currentTime;
    // A gentle fade-in so starting the score is never a pop.
    this.padGain = ctx.createGain();
    this.padGain.gain.setValueAtTime(0.0001, now);
    this.padGain.gain.linearRampToValueAtTime(spec.padGain, now + 3);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 900;
    this.padGain.connect(filter).connect(this.dest);
    this.padOscs = [0, 1, 2].map(() => {
      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.connect(this.padGain!);
      osc.start();
      return osc;
    });
    this.nextChordAt = now + 0.1;
    this.nextPluckAt = now + 1.5 + this.random();
    this.schedulePad(spec, now + LOOKAHEAD);
    this.schedulePlucks(spec, now + LOOKAHEAD);
    this.timer = setInterval(() => {
      if (this.stopped) return;
      const t = ctx.currentTime;
      this.schedulePad(spec, t + LOOKAHEAD);
      this.schedulePlucks(spec, t + LOOKAHEAD);
    }, 2000);
  }

  private schedulePad(spec: MoodSpec, horizon: number): void {
    const chord = spec.padChords[this.chordIndex % spec.padChords.length]!;
    while (this.nextChordAt < horizon) {
      const t = this.nextChordAt;
      this.padOscs.forEach((osc, i) => {
        const freq = chord[i]! * (1 + (this.random() - 0.5) * 0.004);
        osc.frequency.setTargetAtTime(freq, t, 1.2);
      });
      this.chordIndex += 1;
      this.nextChordAt += 16;
    }
  }

  private schedulePlucks(spec: MoodSpec, horizon: number): void {
    const ctx = this.ctx;
    while (this.nextPluckAt < horizon) {
      const t = this.nextPluckAt;
      const freq = spec.plucks[Math.floor(this.random() * spec.plucks.length)]!;
      const amp = ctx.createGain();
      amp.gain.setValueAtTime(0.12, t);
      amp.gain.exponentialRampToValueAtTime(0.001, t + 1.4);
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq * (1 + (this.random() - 0.5) * 0.01);
      osc.connect(amp).connect(this.padGain ?? this.dest);
      osc.start(t);
      osc.stop(t + 1.5);
      // Rests are part of the music: sometimes skip a beat.
      this.nextPluckAt += spec.pluckEvery * (this.random() < 0.25 ? 2 : 1) * (0.8 + this.random() * 0.5);
    }
  }

  /** Fade out over a second, then silence the oscillators. Safe to call twice. */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    if (this.timer !== null) clearInterval(this.timer);
    const t = this.ctx.currentTime;
    this.padGain?.gain.cancelScheduledValues(t);
    this.padGain?.gain.setValueAtTime(Math.max(0.0001, this.padGain.gain.value), t);
    this.padGain?.gain.linearRampToValueAtTime(0.0001, t + 1);
    const oscs = this.padOscs;
    const ctx = this.ctx;
    setTimeout(() => {
      for (const osc of oscs) osc.stop();
      void ctx;
    }, 1100);
  }
}
